import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { ActKc2CorrectionUpdateInput, ActKc2Data, WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { Loader } from 'uikit';
import { getDateIncludingCurrentTime } from 'utils/formatDateToLocale';

import { useCallback, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { ActKC2EditForm } from '../AddActKC2/ActKC2EditForm';
import type { IActKC2Data } from '../AddActKC2/EditActKC2Modal';
import { parseActKC2 } from '../AddActKC2/EditActKC2Modal';
import { useGetActKc2 } from '../hooks/useGetActKc2';

import { useUerUpdateActKC2Correction } from './hooks/useUerUpdateActKC2Correction';

interface IEditActKc2Correction {
    actKc2?: ActKc2Data;
    actKC2Id?: number;
    onClose: (hasSaved: boolean) => void;
    aktKC2Rules: IUseAccess['aktKC2'];
    statusWorkOrder?: WorkOrderStatusEnum;
}

export const EditActKc2Correction: React.FC<IEditActKc2Correction> = (props) => {
    const { actKc2, actKC2Id, onClose, statusWorkOrder, aktKC2Rules } = props;

    const formRef = useRef<HTMLFormElement>(null);

    const { getActKC2Response, getActKC2Reset } = useGetActKc2(actKC2Id);

    const onUpdateSuccess = useCallback(() => {
        getActKC2Reset(undefined);
        onClose(true);
    }, [getActKC2Reset, onClose]);

    const { uerUpdateActKC2CorrectionMakeRequest, uerUpdateActKC2CorrectionStatus } = useUerUpdateActKC2Correction({
        onSuccess: onUpdateSuccess,
    });

    const [isDirtyFields, setIsDirtyFields] = useState<boolean>(false);

    const onCloseWithoutSaving = useCallback(() => {
        getActKC2Reset(undefined);
        onClose(false);
    }, [getActKC2Reset, onClose]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onCloseWithoutSaving,
        });

    const onEditCancel = useCallback(() => {
        if (isDirtyFields) {
            handleOpenConfirmationDialog();
        } else {
            getActKC2Reset(undefined);
            onClose(false);
        }
    }, [getActKC2Reset, handleOpenConfirmationDialog, isDirtyFields, onClose]);

    const onConfirm = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, []);

    const actualActKc2 = useMemo(() => {
        const data = actKc2 || getActKC2Response?.uerActKC2?.data;

        if (!data) {
            return data;
        }

        return {
            ...parseActKC2(data),
            constructionType: data?.object?.constructionType?.fullName,
            objectName: data?.object?.name,
        } as IActKC2Data;
    }, [actKc2, getActKC2Response?.uerActKC2?.data]);

    const constructionTypeCode = useMemo(
        () => actualActKc2?.object?.constructionType?.code,
        [actualActKc2?.object?.constructionType?.code],
    );

    const onSubmit = useCallback(
        (values: any) => {
            const input: ActKc2CorrectionUpdateInput = {
                correctionDate: values?.correctionDate
                    ? getDateIncludingCurrentTime(values?.correctionDate)
                    : values?.correctionDate,
                id: actualActKc2?.id,
                status: values?.status?.id,
            };

            uerUpdateActKC2CorrectionMakeRequest(input);
        },
        [actualActKc2?.id, uerUpdateActKC2CorrectionMakeRequest],
    );

    return (
        <>
            <ModalDialog
                title="Реквизиты корректировочного Акта КС-2"
                isOpen
                hasOverlay
                withTitleCloseButton
                onClose={onEditCancel}
                onConfirm={onConfirm}
                isCommit={uerUpdateActKC2CorrectionStatus === RequestStatus.FETCHING}
            >
                <div id="addActKC2">
                    {!actualActKc2 ? (
                        <Loader />
                    ) : (
                        <ActKC2EditForm
                            defaultValues={actualActKc2}
                            onSubmit={onSubmit}
                            constructionTypeCode={constructionTypeCode}
                            actKC2Id={actKC2Id}
                            setIsDirty={setIsDirtyFields}
                            workOrderObjectId={getActKC2Response?.uerActKC2?.data?.object?.id}
                            aktKC2Rules={aktKC2Rules}
                            statusWorkOrder={statusWorkOrder}
                            ref={formRef}
                            isCorrection
                        />
                    )}
                </div>
            </ModalDialog>
            <ConfirmationDialog
                textHeader="ОТМЕНА РЕДАКТИРОВАНИЯ КОРРЕКТИРОВОЧНОГО АКТА КС-2"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </>
    );
};

EditActKc2Correction.displayName = 'EditActKc2Correction';
